/**
 * Send Activity Email Modal
 *
 * Modal for sending an email alert about a specific timeline activity.
 * Supports multi-recipient (stakeholder toggle + custom emails),
 * optional document attachments, and a free-text note.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Mail01, Send01, Users01, File06, XClose, Plus } from '@untitledui/icons';
import { useMutation } from '@tanstack/react-query';
import { ModalOverlay, Modal, Dialog } from '@/components/application/modals/modal';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { CloseButton } from '@/components/base/buttons/close-button';
import { Badge } from '@/components/base/badges/badges';
import { CheckboxBase } from '@/components/base/checkbox/checkbox';
import { toast } from '@/lib/toast-service';
import { apiClient } from '@/lib/api-client';
import {
    useContainerStakeholders,
    type StakeholderInfo,
} from '@/hooks/api/use-container-stakeholders';
import { useContainerDocuments } from '@/hooks/api/use-documents';
import type { BackendActivity } from '@/data/api/activities';
import { stripUuids } from '@/utils/strip-uuids';

// ============================================================================
// API
// ============================================================================

interface SendActivityAlertRequest {
    activity_id?: string;
    issue_id?: string;
    container_id: string;
    to_emails: string[];
    note?: string;
    language?: string;
    document_ids?: string[];
    subject?: string;
}

interface SendEmailResponse {
    success: boolean;
    message_id?: string;
    status_code: number;
    error?: string;
}

async function sendActivityAlert(data: SendActivityAlertRequest): Promise<SendEmailResponse> {
    return apiClient<SendEmailResponse>('/mailer/send-activity-alert', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

function useSendActivityAlert() {
    return useMutation({
        mutationFn: sendActivityAlert,
    });
}

// ============================================================================
// Helpers
// ============================================================================

function isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function formatFileSize(bytes: number | null): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
}

// ============================================================================
// Component
// ============================================================================

interface SendActivityEmailModalProps {
    isOpen: boolean;
    onClose: () => void;
    activity?: BackendActivity;
    /** Direct issue props — alternative to passing an activity */
    issueId?: string;
    issueTitle?: string;
    issueSummary?: string;
    containerId: string;
}

export function SendActivityEmailModal({
    isOpen,
    onClose,
    activity,
    issueId,
    issueTitle,
    issueSummary,
    containerId,
}: SendActivityEmailModalProps) {
    const aboutTitle = activity?.title ?? issueTitle ?? '';
    const aboutSummary = activity?.summary ?? issueSummary ?? '';
    const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
    const [customEmails, setCustomEmails] = useState<string[]>([]);
    const [customEmailInput, setCustomEmailInput] = useState('');
    const [subject, setSubject] = useState('');
    const [note, setNote] = useState('');
    const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());

    const sendAlert = useSendActivityAlert();

    // Fetch stakeholders for recipient selection
    const { data: stakeholderData } = useContainerStakeholders(containerId, {
        enabled: isOpen,
    });

    // Fetch container documents for attachment picker
    const { data: containerDocs } = useContainerDocuments(isOpen ? containerId : undefined);

    // Filter stakeholders that have emails
    const emailableStakeholders = useMemo(() => {
        if (!stakeholderData?.stakeholders) return [];
        return stakeholderData.stakeholders.filter(
            (s): s is StakeholderInfo & { email: string } => !!s.email
        );
    }, [stakeholderData]);

    // Auto-fill subject and select primary stakeholder on open
    useEffect(() => {
        if (isOpen && !subject) {
            setSubject(aboutTitle);
        }
    }, [isOpen, subject, aboutTitle]);

    useEffect(() => {
        if (isOpen && emailableStakeholders.length > 0 && selectedEmails.size === 0) {
            const primary = emailableStakeholders[0];
            setSelectedEmails(new Set([primary.email]));
        }
    }, [isOpen, emailableStakeholders, selectedEmails.size]);

    // Reset on close
    useEffect(() => {
        if (!isOpen) {
            setSelectedEmails(new Set());
            setCustomEmails([]);
            setCustomEmailInput('');
            setSubject('');
            setNote('');
            setSelectedDocIds(new Set());
        }
    }, [isOpen]);

    // All recipient emails combined
    const allRecipients = useMemo(() => {
        const emails = new Set<string>();
        for (const e of selectedEmails) emails.add(e);
        for (const e of customEmails) emails.add(e);
        return [...emails];
    }, [selectedEmails, customEmails]);

    const toggleStakeholder = useCallback((email: string) => {
        setSelectedEmails((prev) => {
            const next = new Set(prev);
            if (next.has(email)) {
                next.delete(email);
            } else {
                next.add(email);
            }
            return next;
        });
    }, []);

    const addCustomEmail = useCallback(() => {
        const email = customEmailInput.trim();
        if (!email) return;
        if (!isValidEmail(email)) {
            toast.warning('Please enter a valid email address');
            return;
        }
        if (customEmails.includes(email) || selectedEmails.has(email)) {
            toast.warning('This email is already added');
            return;
        }
        setCustomEmails((prev) => [...prev, email]);
        setCustomEmailInput('');
    }, [customEmailInput, customEmails, selectedEmails]);

    const removeCustomEmail = useCallback((email: string) => {
        setCustomEmails((prev) => prev.filter((e) => e !== email));
    }, []);

    const toggleDocument = useCallback((docId: string) => {
        setSelectedDocIds((prev) => {
            const next = new Set(prev);
            if (next.has(docId)) {
                next.delete(docId);
            } else {
                next.add(docId);
            }
            return next;
        });
    }, []);

    const handleSend = () => {
        if (allRecipients.length === 0) {
            toast.warning('Please select or enter at least one recipient');
            return;
        }

        sendAlert.mutate(
            {
                activity_id: activity?.activity_id,
                issue_id: issueId,
                container_id: containerId,
                to_emails: allRecipients,
                subject: subject.trim() || undefined,
                note: note.trim() || undefined,
                document_ids: selectedDocIds.size > 0 ? [...selectedDocIds] : undefined,
            },
            {
                onSuccess: (response) => {
                    if (response.success) {
                        const count = allRecipients.length;
                        toast.success(
                            count === 1
                                ? `Email sent to ${allRecipients[0]}`
                                : `Email sent to ${count} recipients`
                        );
                        onClose();
                    } else {
                        toast.error(response.error || 'Failed to send email');
                    }
                },
                onError: (err) => {
                    toast.error(err);
                },
            }
        );
    };

    const handleCustomEmailSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        addCustomEmail();
    };

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && onClose()} isDismissable>
            <Modal className="max-w-lg">
                <Dialog>
                    <div className="w-full rounded-xl bg-primary shadow-xl">
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-secondary px-5 py-4">
                            <div className="flex items-center gap-2">
                                <Mail01 className="h-5 w-5 text-brand-600" />
                                <h2 className="text-lg font-semibold text-primary">Send email alert</h2>
                            </div>
                            <CloseButton onPress={onClose} />
                        </div>

                        <div className="max-h-[70vh] overflow-y-auto px-5 py-4 space-y-4">
                            {/* Activity preview */}
                            <div className="rounded-lg bg-secondary px-3 py-2">
                                <p className="text-xs font-medium text-tertiary mb-0.5">About</p>
                                <p className="text-sm font-medium text-primary truncate">{aboutTitle ? stripUuids(aboutTitle) : ''}</p>
                                <p className="text-xs text-tertiary line-clamp-2 mt-0.5">{aboutSummary ? stripUuids(aboutSummary) : ''}</p>
                            </div>

                            {/* Subject */}
                            <Input
                                label="Subject"
                                placeholder="Email subject..."
                                value={subject}
                                onChange={setSubject}
                            />

                            {/* Stakeholder multi-select */}
                            {emailableStakeholders.length > 0 && (
                                <div>
                                    <div className="flex items-center gap-1.5 mb-2">
                                        <Users01 className="h-3.5 w-3.5 text-tertiary" />
                                        <p className="text-xs font-medium text-secondary">Stakeholders</p>
                                    </div>
                                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                        {emailableStakeholders.map((s, i) => {
                                            const isSelected = selectedEmails.has(s.email);
                                            return (
                                                <button
                                                    key={`${s.role}-${s.email}`}
                                                    type="button"
                                                    onClick={() => toggleStakeholder(s.email)}
                                                    className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors ${
                                                        isSelected
                                                            ? 'border-brand-300 bg-secondary ring-1 ring-brand-300'
                                                            : 'border-secondary hover:bg-secondary'
                                                    }`}
                                                >
                                                    <CheckboxBase isSelected={isSelected} className="relative" />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <p className="text-sm font-medium text-primary truncate">
                                                                {s.contact_name || s.company_name || s.email}
                                                            </p>
                                                            {i === 0 && (
                                                                <Badge color="brand" size="sm">Primary</Badge>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-0.5">
                                                            <Badge color="gray" size="sm">{s.role}</Badge>
                                                            <span className="text-xs text-tertiary truncate">{s.email}</span>
                                                        </div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Custom emails */}
                            <div>
                                <p className="text-xs font-medium text-secondary mb-1.5">Additional recipients</p>
                                {customEmails.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                        {customEmails.map((email) => (
                                            <span
                                                key={email}
                                                className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs font-medium text-primary"
                                            >
                                                {email}
                                                <button
                                                    type="button"
                                                    onClick={() => removeCustomEmail(email)}
                                                    className="text-tertiary hover:text-primary"
                                                >
                                                    <XClose className="h-3 w-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <form onSubmit={handleCustomEmailSubmit} className="flex gap-2">
                                    <div className="flex-1">
                                        <Input
                                            placeholder="recipient@company.com"
                                            value={customEmailInput}
                                            onChange={setCustomEmailInput}
                                            type="email"
                                            icon={Mail01}
                                        />
                                    </div>
                                    <Button
                                        size="sm"
                                        color="secondary"
                                        type="submit"
                                        isDisabled={!customEmailInput.trim()}
                                        iconLeading={Plus}
                                        className="shrink-0 self-end"
                                    >
                                        Add
                                    </Button>
                                </form>
                            </div>

                            {/* Document attachments */}
                            {containerDocs && containerDocs.length > 0 && (
                                <div>
                                    <div className="flex items-center gap-1.5 mb-2">
                                        <File06 className="h-3.5 w-3.5 text-tertiary" />
                                        <p className="text-xs font-medium text-secondary">
                                            Attach documents
                                            {selectedDocIds.size > 0 && (
                                                <span className="ml-1 text-brand-600">({selectedDocIds.size})</span>
                                            )}
                                        </p>
                                    </div>
                                    <div className="space-y-1 max-h-36 overflow-y-auto rounded-lg border border-secondary p-2">
                                        {containerDocs.map((doc) => {
                                            const isChecked = selectedDocIds.has(doc.document_id);
                                            return (
                                                <button
                                                    key={doc.document_id}
                                                    type="button"
                                                    onClick={() => toggleDocument(doc.document_id)}
                                                    className={`flex w-full items-center gap-2.5 rounded-lg border px-2 py-1.5 text-left transition-colors ${
                                                        isChecked
                                                            ? 'border-brand-300 bg-secondary ring-1 ring-brand-300'
                                                            : 'border-transparent hover:bg-secondary'
                                                    }`}
                                                >
                                                    <CheckboxBase isSelected={isChecked} className="relative" />
                                                    <File06 className="h-3.5 w-3.5 shrink-0 text-tertiary" />
                                                    <span className="flex-1 truncate text-sm text-primary">
                                                        {doc.file_name || 'Untitled'}
                                                    </span>
                                                    {doc.document_type && (
                                                        <Badge color="gray" size="sm">{doc.document_type}</Badge>
                                                    )}
                                                    {doc.file_size_bytes && (
                                                        <span className="text-xs text-tertiary shrink-0">
                                                            {formatFileSize(doc.file_size_bytes)}
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Note */}
                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-secondary">
                                    Add a note (optional)
                                </label>
                                <textarea
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="Additional context or instructions..."
                                    rows={3}
                                    className="w-full rounded-lg border border-secondary bg-primary px-3 py-2 text-sm text-primary placeholder:text-placeholder focus:outline-none focus:ring-1 focus:ring-brand-500"
                                />
                            </div>

                            {/* Send button */}
                            <Button
                                color="primary"
                                className="w-full"
                                onPress={handleSend}
                                isLoading={sendAlert.isPending}
                                isDisabled={allRecipients.length === 0}
                                iconLeading={Send01}
                            >
                                {allRecipients.length === 0
                                    ? 'Select recipients...'
                                    : allRecipients.length === 1
                                        ? `Send to ${allRecipients[0]}`
                                        : `Send to ${allRecipients.length} recipients`}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}
